import { ethers } from 'ethers';
import GovernanceContractABI from './GovernanceContract.json';

// Governance Contract ABI
export const GOVERNANCE_ABI = GovernanceContractABI;

// Proposal State Enum
export enum ProposalState {
  Pending = 0,
  Active = 1,
  Canceled = 2,
  Defeated = 3,
  Succeeded = 4,
  Queued = 5,
  Expired = 6,
  Executed = 7
}

// Vote Support Types
export enum VoteType {
  Against = 0,
  For = 1,
  Abstain = 2
}

// Proposal Interface
export interface Proposal {
  id: string;
  proposer: string;
  targets: string[];
  values: string[];
  signatures: string[];
  calldatas: string[];
  voteStart: string;
  voteEnd: string;
  description: string;
  state: ProposalState;
  forVotes: string;
  againstVotes: string;
  abstainVotes: string;
}

// Contract Factory Functions
export const getGovernanceContract = (provider: ethers.Provider, contractAddress: string) => {
  return new ethers.Contract(contractAddress, GOVERNANCE_ABI, provider);
};

export const getGovernanceSignerContract = (signer: ethers.Signer, contractAddress: string) => {
  return new ethers.Contract(contractAddress, GOVERNANCE_ABI, signer);
};

// Utility Functions
export const getStateString = (state: ProposalState): string => {
  switch (state) {
    case ProposalState.Pending:
      return 'Pending';
    case ProposalState.Active:
      return 'Active';
    case ProposalState.Canceled:
      return 'Canceled';
    case ProposalState.Defeated:
      return 'Defeated';
    case ProposalState.Succeeded:
      return 'Succeeded';
    case ProposalState.Queued:
      return 'Queued';
    case ProposalState.Expired:
      return 'Expired';
    case ProposalState.Executed:
      return 'Executed';
    default:
      return 'Unknown';
  }
};

export const getVoteTypeString = (voteType: VoteType): string => {
  switch (voteType) {
    case VoteType.Against:
      return 'Against';
    case VoteType.For:
      return 'For';
    case VoteType.Abstain:
      return 'Abstain';
    default:
      return 'Unknown';
  }
};

export const formatTimestamp = (timestamp: string): string => {
  if (timestamp === '0') {
    return 'Not set';
  }
  
  const date = new Date(Number(timestamp) * 1000);
  return date.toLocaleString();
};


export const formatVotes = (votes: string, decimals: number = 18): string => {
  return ethers.formatUnits(votes, decimals);
};

export const formatVotesCompact = (votes: string, decimals: number = 18): string => {
  const formatted = ethers.formatUnits(votes, decimals);
  const num = parseFloat(formatted);
  
  if (num >= 1000000) {
    return (num / 1000000).toFixed(2) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(2) + 'K';
  } else {
    return num.toFixed(2);
  }
};

export const parseVotes = (votes: string, decimals: number = 18): string => {
  return ethers.parseUnits(votes, decimals).toString();
};

// Validation function
export const validateGovernanceContract = async (provider: ethers.Provider, contractAddress: string): Promise<boolean> => {
  try {
    const contract = getGovernanceContract(provider, contractAddress);
    // Try to call the name() function to verify it's a valid governance contract
    const name = await contract.name();
    return typeof name === 'string' && name.length > 0;
  } catch (error) {
    console.warn('Governance contract validation failed:', error);
    return false;
  }
};
